import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import '@/styles/Common/Tooltip.less';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
/**
 * Универсальный компонент Tooltip для отображения подсказок при наведении.
 * Минималистичный дизайн, плавная анимация, некликабельный.
 */
const Tooltip = ({ children, text, position = 'top', delay = 200 }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const timeoutRef = useRef(null);
    const wrapperRef = useRef(null);
    const tooltipRef = useRef(null);
    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            setIsVisible(true);
        }, delay);
    };
    const handleMouseLeave = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setIsVisible(false);
    };
    useEffect(() => {
        if (isVisible && wrapperRef.current && tooltipRef.current) {
            const updatePosition = () => {
                if (!wrapperRef.current || !tooltipRef.current)
                    return;
                const wrapperRect = wrapperRef.current.getBoundingClientRect();
                const tooltipRect = tooltipRef.current.getBoundingClientRect();
                const gap = 8;
                let top = 0;
                let left = 0;
                switch (position) {
                    case 'top':
                        top = wrapperRect.top - tooltipRect.height - gap;
                        left = wrapperRect.left + wrapperRect.width / 2 - tooltipRect.width / 2;
                        break;
                    case 'bottom':
                        top = wrapperRect.bottom + gap;
                        left = wrapperRect.left + wrapperRect.width / 2 - tooltipRect.width / 2;
                        break;
                    case 'left':
                        top = wrapperRect.top + wrapperRect.height / 2 - tooltipRect.height / 2;
                        left = wrapperRect.left - tooltipRect.width - gap;
                        break;
                    case 'right':
                        top = wrapperRect.top + wrapperRect.height / 2 - tooltipRect.height / 2;
                        left = wrapperRect.right + gap;
                        break;
                }
                // Корректировка позиции, чтобы tooltip не выходил за границы экрана
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                if (left < 0)
                    left = 8;
                if (left + tooltipRect.width > viewportWidth) {
                    left = viewportWidth - tooltipRect.width - 8;
                }
                if (top < 0)
                    top = 8;
                if (top + tooltipRect.height > viewportHeight) {
                    top = viewportHeight - tooltipRect.height - 8;
                }
                setTooltipPosition({ top, left });
            };
            // Используем requestAnimationFrame для корректного расчета размеров после рендера
            requestAnimationFrame(() => {
                updatePosition();
            });
        }
    }, [isVisible, position]);
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);
    return (_jsxs(_Fragment, { children: [_jsx("div", { ref: wrapperRef, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, style: { display: 'inline-block' }, children: children }), isVisible &&
                createPortal(_jsx("div", { ref: tooltipRef, className: "tooltip-wrapper", style: {
                        position: 'fixed',
                        top: `${tooltipPosition.top}px`,
                        left: `${tooltipPosition.left}px`,
                        zIndex: 10000,
                        pointerEvents: 'none',
                    }, children: _jsx("div", { className: "tooltip-content", children: text }) }), document.body)] }));
};
export default Tooltip;
